<template>
  <div>
    <v-row>
      <v-col v-if="!isOrganizationManager" cols="12" sm="6" md>
        <v-autocomplete
          :disabled="loading"
          hide-details
          v-model="organization"
          :items="organizations"
          item-value="_id"
          outlined
          dense
          background-color="white"
          :label="$t('user.registration.organization')"
          class="rounded-lg"
          clearable
          :filter="customLocalisedAutoCompleteFilter"
        >
          <template v-slot:selection="{ item }">
            {{ getLocalisedValue(item.name) }}
          </template>

          <template v-slot:item="{ item }">
            <div class="d-flex justify-start">
              {{ getLocalisedValue(item.name) }}
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6" md>
        <v-autocomplete
          :disabled="loading"
          hide-details
          v-model="training"
          :items="trainings"
          item-value="_id"
          outlined
          dense
          background-color="white"
          :label="$t('user.registration.training')"
          class="rounded-lg"
          clearable
          :filter="customLocalisedAutoCompleteFilter"
        >
          <template v-slot:selection="{ item }">
            {{ getLocalisedValue(item.title) }}
          </template>

          <template v-slot:item="{ item }">
            <div class="d-flex justify-start">
              {{ getLocalisedValue(item.title) }}
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6" md>
        <v-autocomplete
          :disabled="loading"
          hide-details
          v-model="employee"
          :items="employees"
          item-value="_id"
          item-text="name"
          outlined
          dense
          background-color="white"
          :label="$t('reports.employee')"
          class="rounded-lg"
          clearable
          auto-select-first
          :filter="customLocalisedAutoCompleteFilter"
        >
          <template v-slot:item="{ item }">
            <div class="d-flex justify-start">
              {{ item.name }}
              {{ item.user && item.user.email ? `(${item.user.email})` : "" }}
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6" md>
        <v-menu
          v-model="startDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :disabled="loading"
              hide-details
              v-model="formattedStartDate"
              :label="$t('reports.chooseFromDate')"
              class="rounded-lg"
              outlined
              dense
              background-color="white"
              readonly
              v-bind="attrs"
              v-on="on"
              clearable
            >
              <v-icon slot="append" color="textLight">
                mdi-calendar-blank-outline
              </v-icon></v-text-field
            >
          </template>

          <v-date-picker
            color="primary"
            v-model="startDate"
            no-title
            @input="startDateMenu = false"
          ></v-date-picker>
        </v-menu>
      </v-col>

      <v-col cols="12" sm="6" md>
        <v-menu
          v-model="endDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :disabled="loading"
              hide-details
              class="rounded-lg"
              :label="$t('reports.chooseToDate')"
              v-model="formattedEndDate"
              outlined
              dense
              background-color="white"
              readonly
              v-bind="attrs"
              v-on="on"
              clearable
            >
              <v-icon slot="append" color="textLight">
                mdi-calendar-blank-outline
              </v-icon></v-text-field
            >
          </template>

          <v-date-picker
            color="primary"
            v-model="endDate"
            no-title
            @input="endDateMenu = false"
          ></v-date-picker>
        </v-menu>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: "CertificatesTableFilters",
  props: {
    loading: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      organization: null,
      training: null,
      employee: null,
      organizations: [],
      trainings: [],
      employeeList: [],
      startDate: null,
      endDate: null,
      startDateMenu: false,
      endDateMenu: false,
    };
  },
  computed: {
    isOrganizationManager() {
      return (
        this.$store.state.authenticatedUser?.isOrganizationManager ?? false
      );
    },
    employees() {
      return this.employeeList.reduce((result, employee) => {
        const obj = {
          ...employee,
          name: `${employee?.user?.firstName || ""} ${
            employee?.user?.lastName || ""
          }`,
        };

        if (this.organization) {
          if (employee.organization?._id === this.organization) {
            result.push(obj);
          }
        } else {
          result.push(obj);
        }

        return result;
      }, []);
    },
    formattedStartDate: {
      get() {
        return this.startDate
          ? this.$moment(this.startDate).format("DD/MM/YYYY")
          : "";
      },
      set(value) {
        this.startDate = value;
      },
    },
    formattedEndDate: {
      get() {
        return this.endDate
          ? this.$moment(this.endDate).format("DD/MM/YYYY")
          : "";
      },
      set(value) {
        this.endDate = value;
      },
    },
  },
  watch: {
    organization() {
      this.$emit("filter", "organization", this.organization);
      if (this.organization) this.employee = null;
    },
    training() {
      this.$emit("filter", "training", this.training);
    },
    employee() {
      this.$emit("filter", "employee", this.employee);
    },
    startDate() {
      if (this.startDate && this.endDate) {
        this.$emit("filter", "dates", {
          startDate: this.startDate,
          endDate: this.endDate,
        });
      } else if (!this.startDate || !this.endDate) {
        this.$emit("filter", "dates", {
          startDate: null,
          endDate: null,
        });
      }
    },
    endDate() {
      if (this.startDate && this.endDate) {
        this.$emit("filter", "dates", {
          startDate: this.startDate,
          endDate: this.endDate,
        });
      } else if (!this.startDate || !this.endDate) {
        this.$emit("filter", "dates", {
          startDate: null,
          endDate: null,
        });
      }
    },
  },
  apollo: {
    getOrganizations() {
      return {
        query: require("../../../organization-module/api/query/getOrganizations.gql"),
        variables: { limit: 0, skip: 0, filterInput: null },
        result({ data }) {
          if (data?.getOrganizations?.organizations) {
            this.organizations = data.getOrganizations.organizations;
          }
        },
      };
    },
    getTrainings() {
      return {
        query: require("../../../course-module/api/query/getTrainings.gql"),
        variables: { limit: 0, skip: 0 },
        result({ data }) {
          if (data?.getTrainings?.trainings) {
            this.trainings = data.getTrainings.trainings;
          }
        },
      };
    },
    getEmployees() {
      return {
        query: require("../../../user-module/api/query/getEmployees.gql"),
        variables: { limit: 0, skip: 0 },
        result({ data }) {
          if (data?.getEmployees) {
            this.employeeList = data.getEmployees.employees;
          }
        },
      };
    },
  },
};
</script>
